import axios from 'axios';

const API_URL = import.meta.env.VITE_CONTENT_UPLOADER_API_URL ?? '/api';

// The entire content-uploader client acts as the CONTENT_UPLOADER application:
// auth tokens are only minted against this application, and every media-library
// request is self-scoped by the backend to the authenticated creator.
export const CONTENT_UPLOADER_APPLICATION = 'CONTENT_UPLOADER';

const STORAGE_TOKEN = 'content_uploader_token';
const STORAGE_REFRESH = 'content_uploader_refresh_token';

export const http = axios.create({
  baseURL: API_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    'X-Application': CONTENT_UPLOADER_APPLICATION,
  },
});

http.interceptors.request.use((config) => {
  const token = localStorage.getItem(STORAGE_TOKEN);
  if (token !== null) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

// ---------------------------------------------------------------------------
// 401 handling: refresh the access token via /v1/identity/refresh and retry.
// Falls back to a local sign-out + redirect to /login when refresh fails or
// no refresh token is available, so protected requests never linger with a
// dead session.
// ---------------------------------------------------------------------------

let isRefreshing = false;
let pendingQueue: Array<{
  resolve: (token: string) => void;
  reject: (err: unknown) => void;
}> = [];

function processQueue(err: unknown, token: string | null) {
  for (const { resolve, reject } of pendingQueue) {
    if (err !== null && err !== undefined) {
      reject(err);
    } else {
      resolve(token as string);
    }
  }
  pendingQueue = [];
}

function clearLocalAuth() {
  localStorage.removeItem(STORAGE_TOKEN);
  localStorage.removeItem(STORAGE_REFRESH);
  localStorage.removeItem('content_uploader_user');
  localStorage.removeItem('content_uploader_org');
  localStorage.removeItem('content_uploader_app');
}

function redirectToLogin() {
  if (!window.location.pathname.startsWith('/login')) {
    window.location.assign('/login');
  }
}

interface RefreshResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

http.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config as
      | (import('axios').InternalAxiosRequestConfig & { _retry?: boolean })
      | undefined;

    if (
      error.response?.status !== 401 ||
      originalRequest === undefined ||
      originalRequest._retry === true
    ) {
      return Promise.reject(error);
    }

    const refreshToken = localStorage.getItem(STORAGE_REFRESH);
    if (refreshToken === null) {
      clearLocalAuth();
      redirectToLogin();
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise<string>((resolve, reject) => {
        pendingQueue.push({ resolve, reject });
      }).then((token) => {
        originalRequest.headers['Authorization'] = `Bearer ${token}`;
        return http(originalRequest);
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      const { data } = await axios.post<RefreshResponse>(
        `${API_URL}/v1/identity/refresh`,
        { refreshToken },
        {
          headers: {
            'Content-Type': 'application/json',
            'X-Application': CONTENT_UPLOADER_APPLICATION,
          },
        },
      );

      localStorage.setItem(STORAGE_TOKEN, data.accessToken);
      localStorage.setItem(STORAGE_REFRESH, data.refreshToken);

      processQueue(null, data.accessToken);

      originalRequest.headers['Authorization'] = `Bearer ${data.accessToken}`;
      return http(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError, null);
      clearLocalAuth();
      redirectToLogin();
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  },
);
